/**
 * My readings: which bhavas support or test each navagraha.
 * House lists follow common Jyotish teaching threads; prose is original (rephrased for study).
 */
import { hindiDigitOrdinal } from '../utils/hindiDigitOrdinal';

/** @typedef {Object} GrahaBhavaFitRow
 * @property {string} planet
 * @property {string} headlineHi
 * @property {string} headlineEn
 * @property {number[]} favorable
 * @property {string} favorableHi
 * @property {string} favorableEn
 * @property {number[]} challenging
 * @property {string} challengingHi
 * @property {string} challengingEn
 * @property {boolean} [upachayaFavorable]
 */

export const GRAHA_BHAVA_INTRO = {
  titleHi: 'ग्रहों के लिए कौन से भाव अच्छे, कौन से मुश्किल?',
  titleEn: 'Which houses welcome each graha - and which ones test it?',
  leadHi:
    'हर ग्रह की अलग भाषा है। कुछ भाव उसे मंच देते हैं, कुछ मुश्किल रास्ता। यह सारांश शिक्षकों के नोट्स जैसा है - आपकी कुंडली का अंतिम फैसला नहीं।',
  leadEn:
    'Each graha has a temperament: some bhavas act like a stage, others like a steep road. This page gathers recurring teaching notes from charts and threads - not a final verdict on your kundali.',
  disclaimerHi: 'भाव नंबर याद रखने में मदद करते हैं; बल, दृष्टि, दशा और गोचर पूरी कहानी बताते हैं।',
  disclaimerEn: 'House numbers help memory; dignity, aspects, dasha, and transit still tell the full story.',
};

export const GRAHA_BHAVA_INTRO_FACTS = [
  {
    hi: 'उपचय भाव (3, 6, 10, 11) अक्सर पाप ग्रह को मेहनत से फल देते हैं।',
    en: 'Upachaya houses (3, 6, 10, 11) often turn malefic energy into effort that matures into gain.',
  },
  {
    hi: 'दुस्थान (6, 8, 12) किसी भी ग्रह को परख सकते हैं - हर ग्रह की परीक्षा अलग होती है।',
    en: 'Dusthana houses (6, 8, 12) can test any graha, but the flavour of the test changes with the planet.',
  },
  {
    hi: 'एक भाव एक ग्रह के लिए अच्छा, दूसरे के लिए मुश्किल - संदर्भ ग्रह का है।',
    en: 'The same house may suit one graha and strain another - context is always planetary.',
  },
];

/** @type {GrahaBhavaFitRow[]} */
export const GRAHA_BHAVA_FIT_ROWS = [
  {
    planet: 'Sun',
    headlineHi: 'आत्मा का दीपक - जहाँ पहचान और धर्म उगते हैं',
    headlineEn: 'Lamp of the self - brightest where identity and dharma rise',
    favorable: [1, 5, 9, 10],
    favorableHi:
      'लग्न, पंचम, नवम और दशम सूर्य को राज-मार्ग देते हैं: आत्मविश्वास, नेतृत्व, धर्म-भाव और सार्वजनिक चमक मजबूत होती है। यहाँ सूर्य "मैं कौन हूँ और किस दिशा में चलूँ" का उत्तर साफ करता है।',
    favorableEn:
      'Lagna, 5th, 9th, and 10th give Surya a royal road: confidence, leadership, dharma, and public visibility strengthen. Here the Sun answers "who am I, and which way forward?" with clarity.',
    challenging: [6, 8, 12],
    challengingHi:
      '6ठा, 8वाँ और 12वाँ अहंकार और सहनशक्ति की परीक्षा लेते हैं - संघर्ष, छिपी चोट, या आत्म-संदेह की झलक बढ़ सकती है।',
    challengingEn:
      '6th, 8th, and 12th test ego and stamina - conflict, hidden strain, or dips in self-belief may show more strongly.',
  },
  {
    planet: 'Moon',
    headlineHi: 'चंद्रमा की कोमल लहर - घर और हृदय के भाव',
    headlineEn: 'Moon\'s quiet tide - nourished in home and heart houses',
    favorable: [1, 4, 5, 9],
    favorableHi:
      'प्रथम, चतुर्थ, पंचम और नवम में चंद्रमा को विश्राम मिलता है: मन शांत, घर-सुख, रचनात्मक भावना और भावनात्मक पोषण बढ़ते हैं।',
    favorableEn:
      '1st, 4th, 5th, and 9th let Chandra rest and replenish: mental ease, domestic comfort, creativity, and emotional nourishment grow.',
    challenging: [6, 8, 12],
    challengingHi:
      '6ठा, 8वाँ, 12वाँ मानसिक तनाव, मूड-उतार-चढ़ाव और अनावश्यक चिंता की ओर झुका सकते हैं - मन को ज़्यादा "तूफानी समुद्र" जैसा पढ़ा जाता है।',
    challengingEn:
      '6th, 8th, and 12th may tilt toward stress, mood swings, and worry - the mind is read more like a stormy sea.',
  },
  {
    planet: 'Mars',
    headlineHi: 'योद्धा की अग्नि - उपचय में कर्म बनती है',
    headlineEn: 'Warrior fire - upachaya turns heat into motion',
    favorable: [3, 6, 10, 11],
    favorableHi:
      '3रा, 6ठा, 10वाँ और 11वाँ मंगल को ऊर्जा का मैदान देते हैं: साहस, प्रतिस्पर्धा, करियर-धक्का और लाभ-प्रयास में शक्ति। उपचय यहाँ "लड़ो और आगे बढ़ो" का संकेत है।',
    favorableEn:
      '3rd, 6th, 10th, and 11th hand Mars a field of force: courage, rivalry, career drive, and gain through effort. Upachaya here reads as "fight forward."',
    challenging: [4, 7, 8, 12],
    challengingHi:
      'चतुर्थ, सप्तम, अष्टम और द्वादश में क्रोध, टकराव और आवेगी फैसले तेज़ हो सकते हैं - रिश्तों और आंतरिक शांति पर ध्यान देना पड़ता है।',
    challengingEn:
      '4th, 7th, 8th, and 12th can sharpen anger, clash, and impulsive calls - relationships and inner peace need extra care.',
    upachayaFavorable: true,
  },
  {
    planet: 'Mercury',
    headlineHi: 'वाणी और व्यापार - बुद्धि को व्यस्त भाव पसंद',
    headlineEn: 'Speech and trade - wit thrives in busy houses',
    favorable: [2, 5, 6, 10, 11],
    favorableHi:
      'द्वितीय, पंचम, छठा, दशम, एकादश बुध को बाज़ार और कौशल देते हैं: वाणी, व्यापार, तर्क और व्यावहारिक चतुराई को सहारा।',
    favorableEn:
      '2nd, 5th, 6th, 10th, and 11th support Budha with markets and skill: speech, commerce, logic, and practical cleverness.',
    challenging: [8, 12],
    challengingHi:
      'अष्टम और द्वादश में भ्रम, अधिक सोच और हिचकिचाहट भरे निर्णय की चर्चा होती है - मन को एकाग्र रखना काम आता है।',
    challengingEn:
      '8th and 12th are linked with confusion, overthinking, and wobbly decisions - focus becomes the remedy.',
  },
  {
    planet: 'Jupiter',
    headlineHi: 'गुरु की छाया - ज्ञान और पुण्य के भाव',
    headlineEn: 'Jupiter\'s canopy - wisdom swells in merit houses',
    favorable: [1, 2, 5, 9, 11],
    favorableHi:
      'लग्न, द्वितीय, पंचम, नवम, एकादश में गुरु श्रेष्ठ माने जाते हैं: ज्ञान, संपदा, पुण्य, मार्गदर्शन और विस्तार की ऊर्जा बढ़ती है।',
    favorableEn:
      '1st, 2nd, 5th, 9th, and 11th are classic Jupiter seats: wisdom, wealth, merit, guidance, and growth themes strengthen.',
    challenging: [6, 8, 12],
    challengingHi:
      '6ठा, 8वाँ, 12वाँ गलत सलाह, भटकाव या सुनहरे अवसर चूकने जैसी बातें उठा सकते हैं - विश्वास किस पर है, यह देखना पड़ता है।',
    challengingEn:
      '6th, 8th, and 12th may hint at misjudgment, detours, or golden chances slipping past - who you trust matters.',
  },
  {
    planet: 'Venus',
    headlineHi: 'सुख और आकर्षण - प्रेम के भाव',
    headlineEn: 'Comfort and charm - love houses bloom',
    favorable: [4, 5, 7, 12],
    favorableHi:
      'चतुर्थ, पंचम, सप्तम, द्वादश शुक्र को सौंदर्य और संबंध देते हैं: घर-सुख, रोमांस, साझेदारी और कलात्मक आनंद।',
    favorableEn:
      '4th, 5th, 7th, and 12th gift Shukra beauty and bond: home pleasure, romance, partnership, and artistic ease.',
    challenging: [6, 8],
    challengingHi:
      '6ठा और 8वाँ रिश्तों या भौतिक सुख में उतार-चढ़ाव ला सकते हैं - "क्या मिला और क्या खोया" दोनों तरफ़ दिख सकते हैं।',
    challengingEn:
      '6th and 8th can swing relationships or comforts - gains and losses in pleasure may both appear.',
  },
  {
    planet: 'Saturn',
    headlineHi: 'शनि की धैर्य-मूर्ति - मेहनत से बड़ी सफलता',
    headlineEn: 'Saturn\'s slow sculpture - greatness through grind',
    favorable: [3, 6, 10, 11],
    favorableHi:
      '3रा, 6ठा, 10वाँ, 11वाँ में शनि अक्सर "कड़ी मेहनत का फल" पढ़ा जाता है: अनुशासन, सेवा और दीर्घकालिक ऊँचाई उपचय से जुड़ती है।',
    favorableEn:
      '3rd, 6th, 10th, and 11th often read as Saturn\'s harvest through work: discipline, duty, and long arcs of success via upachaya.',
    challenging: [1, 4, 8, 12],
    challengingHi:
      'प्रथम, चतुर्थ, अष्टम, द्वादश में विलंब, भारी जिम्मेदारी और रुकावटें ज़्यादा महसूस हो सकती हैं - शनि यहाँ "धीरे पर गहरा" बोलता है।',
    challengingEn:
      '1st, 4th, 8th, and 12th may feel like delay, heavy load, and obstacles - Saturn speaks "slow but deep" here.',
    upachayaFavorable: true,
  },
  {
    planet: 'Rahu',
    headlineHi: 'राहु की अचानक सीढ़ी - प्रतिष्ठा और विस्तार',
    headlineEn: 'Rahu\'s sudden ladder - fame and outward stretch',
    favorable: [3, 6, 10, 11],
    favorableHi:
      'उपचय भावों में राहु अक्सर तेज़ उन्नति, बाहरी दुनिया में नाम और बड़े नेटवर्क की कहानी सुनाता है - पर अंकुश संयम पर है।',
    favorableEn:
      'In upachaya houses Rahu often tells of quick rise, visibility, and wide outer networks - restraint still decides the ending.',
    challenging: [5, 8, 9],
    challengingHi:
      'पंचम, अष्टम, नवम में भ्रम, लगाव या जोखिम भरे फैसले की चर्चा बढ़ती है - धर्म और बुद्धि की लाइन साफ रखनी पड़ती है।',
    challengingEn:
      '5th, 8th, and 9th may stir confusion, fixation, or risky bets - keep the lines of dharma and judgment clear.',
    upachayaFavorable: true,
  },
  {
    planet: 'Ketu',
    headlineHi: 'केतु की गहरी खामोशी - आध्यात्म और वैराग्य',
    headlineEn: 'Ketu\'s deep hush - spirituality and release',
    favorable: [3, 6, 9, 12],
    favorableHi:
      '3रा, 6ठा, 9वाँ, 12वाँ केतु को आंतरिक यात्रा देते हैं: आध्यात्म, गूढ़ ज्ञान, वैराग्य और मोक्ष-स्वभाव की दिशा।',
    favorableEn:
      '3rd, 6th, 9th, and 12th open Ketu\'s inward path: spirituality, hidden knowing, detachment, and liberation themes.',
    challenging: [2, 5, 7],
    challengingHi:
      'द्वितीय, पंचम, सप्तम में दूरी, अलगाव या भावनात्मक धुंध दिख सकती है - संपत्ति, संतान और साझेदारी जैसे स्पष्ट बंधन कमजोर पढ़े जाते हैं।',
    challengingEn:
      '2nd, 5th, and 7th may show distance, withdrawal, or emotional fog - bonds around wealth, children, and partnership read thinner.',
  },
];

/** @param {number[]} houses */
export function formatBhavaList(houses) {
  return houses.map((n) => `${n}${n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'}`).join(', ');
}

/** @param {number[]} houses */
export function formatBhavaListHi(houses) {
  return houses.map((n) => hindiDigitOrdinal(n)).join(', ');
}
